import type { FC, MouseEvent } from "react";

import { checkIfTierExists } from "@ui/components/TierBadge/TierBadge";
import { useHeaderContext } from "@ui/eva-components/MobileFirstHeader/context";
import { useUser } from "@ui/hooks/useUser";
import { useTranslations } from "@ui/index";

import { useConfig } from "@jti/app-context/useConfig";
import { ChevronForwardIcon } from "@jti/brand-icons/chevron-forward";

import type { AccountDropdownSettings } from "../../../../Header";
import type { NavLinks } from "../../../Navigation";
import { UserAccountBanner, type UserAccountBannerVariant } from "../../../UserAccountBanner";
import { getFullUserName } from "../../../UserActions/utils/getFullUserName";
import { getGreetingTitle } from "../../../UserActions/utils/getGreetingTitle";
import { HeaderNavListItem } from "../HeaderNavListItem";

import styles from "../../MobileNavigation.module.scss";

const TIER_TO_BANNER_VARIANT: Record<string, UserAccountBannerVariant> = {
  silver: "club-tier1",
  gold: "club-tier2",
  black: "club-tier3",
};

const getTierVariant = (tier?: string | null): UserAccountBannerVariant => {
  if (!tier) return "neutral";
  const lower = tier.toLowerCase();
  return checkIfTierExists(lower) ? (TIER_TO_BANNER_VARIANT[lower] ?? "neutral") : "neutral";
};

const buildPointsBalance = (
  tier: string | null | undefined,
  points: number | null | undefined,
  currency: string | undefined,
  tierTranslations?: Record<string, string>
): string => {
  const parts: string[] = [];
  if (tier) {
    const translatedTier = tierTranslations?.[tier.toLowerCase()] ?? tier;
    parts.push(translatedTier);
  }
  const pointsPart = [points?.toString(), currency].filter(Boolean).join(" ");
  if (pointsPart) parts.push(pointsPart);
  return parts.join(" · ");
};

type NavLinksPanelProps = {
  navLinks: NavLinks;
  activePanelId: string;
  accountPanelId: string;
  loyaltyPointsCurrency?: string;
  accountDropdownSettings?: AccountDropdownSettings;
  getSubmenuPanelId: (id: string) => string;
  openPanel: (panelId: string) => (event: MouseEvent<HTMLButtonElement>) => void;
  renderSubmenu?: (id: string) => JSX.Element;
};

export const NavLinksPanel: FC<NavLinksPanelProps> = ({
  navLinks,
  activePanelId,
  accountPanelId,
  loyaltyPointsCurrency,
  accountDropdownSettings,
  getSubmenuPanelId,
  openPanel,
  renderSubmenu,
}) => {
  const { user, isUserLoggedIn } = useUser();
  const { userTier, loyaltyPoints } = useHeaderContext();
  const { __ } = useTranslations();
  const { isSingleNameField, isReversedNameOrder } = useConfig();
  const userName = getFullUserName(__, user, isSingleNameField, isReversedNameOrder);
  const displayName = getGreetingTitle(userName, accountDropdownSettings) ?? userName;

  const hideLoyaltyTiers = accountDropdownSettings?.hideLoyaltyTiers ?? false;

  return (
    <>
      {isUserLoggedIn && user && (
        <div className={styles.header}>
          <UserAccountBanner
            userName={displayName}
            variant={getTierVariant(userTier)}
            pointsBalance={buildPointsBalance(
              hideLoyaltyTiers ? undefined : user.loyalty?.tier,
              loyaltyPoints || user.loyalty?.points,
              loyaltyPointsCurrency,
              accountDropdownSettings?.tierTranslations
            )}
            userDisplayMode="initials"
            initials={userName?.[0]?.toLocaleUpperCase()}
            aria-haspopup="true"
            aria-expanded={activePanelId === accountPanelId}
            aria-controls={accountPanelId}
            onClick={openPanel(accountPanelId)}
          />
        </div>
      )}
      <ul className={styles.list}>
        {navLinks.map((link) => {
          const hasSubmenu = Boolean(link.secondaryNavigation) && Boolean(renderSubmenu);
          const panelId = link.secondaryNavigation ? getSubmenuPanelId(link.secondaryNavigation.id) : "";

          if (hasSubmenu) {
            return (
              <li key={`${link.title}-${link.url}`}>
                <HeaderNavListItem
                  label={link.title}
                  endIcon={ChevronForwardIcon}
                  aria-haspopup="true"
                  aria-expanded={activePanelId === panelId}
                  aria-controls={panelId}
                  onClick={openPanel(panelId)}
                />
              </li>
            );
          }

          return (
            <li key={`${link.title}-${link.url}`}>
              <HeaderNavListItem
                variant="link"
                label={link.title}
                href={link.url}
                aria-current={link.isActive ? "page" : undefined}
              />
            </li>
          );
        })}
      </ul>
    </>
  );
};
